// PatientRow — one patient as a squared-off row: grey rounded card with a
// primary-blue "Token N" badge, a big centered uppercase name, an optional
// room tag, and a solid status pill on the right.
import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import Marquee from "react-fast-marquee";
import { BookingV2 } from "../types";
import { useTVConfig } from "../contexts/TVConfigContext";
import type { Density } from "./DoctorQueuePanel";
import {
  QueueCategory,
  isOngoingStatus,
  queueCategoryOf,
  statusName,
} from "../constants";

// Solid v1 column colors: Inclinic = blue (#0F3CBE), Scan = orange (#EA580C),
// History = violet (#7C3AED). Ongoing overrides to green regardless of queue.
const CATEGORY_COLOR: Record<QueueCategory, string> = {
  Inclinic: "#0F3CBE",
  Scan: "#EA580C",
  History: "#7C3AED",
};

// Token/slot badge — the primary brand blue.
const TOKEN_COLOR = "#0F3CBE";

// A plain queued Inclinic patient shows their booking mode (Booking / Walk-in
// / Reschedule …) rather than the word "Inclinic" — matches v1's column view.
const MODE_LABELS: Record<string, string> = {
  booking: "Booking",
  "walk-in": "Walk-in",
  walkin: "Walk-in",
  followup: "Follow-up",
  "follow-up": "Follow-up",
  reschedule: "Reschedule",
  rescheduled: "Rescheduled",
};

const fmtMode = (mode?: string): string => {
  const m = (mode ?? "").trim().toLowerCase();
  if (!m) return "Booking";
  return MODE_LABELS[m] ?? m.replace(/\b\w/g, (c) => c.toUpperCase());
};

const SLOT_FORMATS = [moment.ISO_8601, "HH:mm:ss", "HH:mm", "h:mm A"];

// Slot-type bookings have no token — show the booked slot window instead.
const fmtSlot = (start?: string, end?: string): string | null => {
  const s = start ? moment(start, SLOT_FORMATS) : null;
  if (!s || !s.isValid()) return null;
  const e = end ? moment(end, SLOT_FORMATS) : null;
  return e && e.isValid()
    ? `${s.format("h:mm")}–${e.format("h:mm A")}`
    : s.format("h:mm A");
};

interface Props {
  booking: BookingV2;
  name: string;
  // Row density, decided by RotatingBoard (see DoctorQueuePanel.Density).
  density?: Density;
  // Patient transferred in from another doctor — their token/slot belong to
  // the original doctor's queue, so we show a "T" badge instead of the token.
  transferred?: boolean;
  // Per-patient room tag. Only shown when the doctor has more than one room
  // (single-room doctors get one doctor-level badge instead — see
  // DoctorQueuePanel). Mirrors v1's multi-room behaviour.
  showRoomTag?: boolean;
}

const PatientRow: React.FC<Props> = ({
  booking,
  name,
  density = "normal",
  transferred,
  showRoomTag,
}) => {
  const tvConfig = useTVConfig();
  const large = density === "large";
  const compact = density === "compact";
  // Honor the same display toggles as v1 (default to showing, except room).
  const showName = tvConfig.patient_name;
  const showToken = tvConfig.token;
  const showSlot = tvConfig.slot;
  const showMode = tvConfig.booking_mode;

  const status = booking.status_id ?? booking.status;
  const category = queueCategoryOf(status);
  const ongoing = isOngoingStatus(status);
  const statusColor = ongoing ? "#10B018" : CATEGORY_COLOR[category];

  // A plain (non-ongoing) Inclinic patient shows their booking mode instead of
  // the word "Inclinic"; everything else keeps its queue status label. The
  // "Booking/Walk-in" toggle only hides the booking-mode pill — real queue
  // statuses (Ongoing, Scan, History) always stay visible (matches v1).
  const isPlainBooking = category === "Inclinic" && !ongoing;
  const rightLabel = isPlainBooking
    ? fmtMode(booking.booking_mode)
    : statusName(status);
  const showRightPill = !isPlainBooking || showMode;

  const hasToken = booking.token_number != null && booking.token_number > 0;
  const slot = fmtSlot(booking.booked_slot_start, booking.booked_slot_end);

  // All rem-based (no px) so the row follows the board's viewport scaling.
  const rowH = large ? "h-20" : compact ? "h-14" : "h-16";
  const pillH = large ? "h-9" : compact ? "h-7" : "h-[1.875rem]";
  const pillText = large ? "text-base" : compact ? "text-xs" : "text-sm";
  // Secondary pills (slot / room / emergency) one step smaller.
  const smallPillText = large
    ? "text-sm"
    : compact
    ? "text-[0.625rem]"
    : "text-xs";
  const nameText = large ? "text-3xl" : compact ? "text-xl" : "text-2xl";
  const pillPx = compact ? "px-2.5" : "px-4";
  const pillPxTight = compact ? "px-2" : "px-3";
  const gap = compact ? "gap-1.5" : "gap-2";

  // Left identifier badge.
  let identifier: React.ReactNode = null;
  if (transferred) {
    identifier = (
      <span
        title="Transferred"
        className={`flex shrink-0 items-center justify-center rounded-lg bg-[#0F3CBE] font-bold text-white ${pillPxTight} ${pillH} ${pillText}`}
      >
        T
      </span>
    );
  } else if (hasToken && showToken && showName) {
    identifier = (
      <span
        className={`flex shrink-0 items-center justify-center whitespace-nowrap rounded-lg font-semibold text-white ${pillPx} ${pillH} ${pillText}`}
        style={{ backgroundColor: TOKEN_COLOR }}
      >
        Token {booking.token_number}
      </span>
    );
  } else if (!hasToken && slot && showSlot) {
    identifier = (
      <span
        className={`flex shrink-0 items-center justify-center whitespace-nowrap rounded-lg font-semibold text-white ${pillPxTight} ${pillH} ${smallPillText}`}
        style={{ backgroundColor: TOKEN_COLOR }}
      >
        {slot}
      </span>
    );
  }

  // Name (falls back to the token when names are hidden).
  const nameLabel = showName
    ? name
    : hasToken
    ? `Token ${booking.token_number}`
    : "";

  // Centre the name; if it's too wide for the row, marquee it right-to-left
  // (v1 behaviour). Measured against a hidden copy so it reacts to real width.
  const nameBoxRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);
  const [overflow, setOverflow] = useState(false);
  useEffect(() => {
    const check = () => {
      if (nameBoxRef.current && measureRef.current) {
        setOverflow(
          measureRef.current.scrollWidth > nameBoxRef.current.clientWidth
        );
      }
    };
    check();
    const t = setTimeout(check, 200);
    window.addEventListener("resize", check);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", check);
    };
  }, [nameLabel, density]);

  return (
    // overflow-hidden: in a narrow column the pills must never poke out past
    // the rounded row edge — the name shrinks (then marquees) instead.
    <div
      className={`flex items-center overflow-hidden rounded-full bg-[#EBEBEB] px-2 ${gap} ${rowH} ${
        ongoing ? "border-2 border-[#10B018]" : ""
      }`}
    >
      {identifier}

      <div
        ref={nameBoxRef}
        className="relative min-w-0 flex-1 overflow-hidden px-2"
      >
        {/* Hidden width probe */}
        <span
          ref={measureRef}
          className={`invisible absolute whitespace-nowrap font-medium uppercase ${nameText}`}
        >
          {nameLabel}
        </span>
        {overflow ? (
          <Marquee play gradient={false} speed={45}>
            <span
              className={`whitespace-nowrap font-medium uppercase text-black ${nameText}`}
            >
              {nameLabel}&nbsp;&nbsp;&nbsp;&nbsp;
            </span>
          </Marquee>
        ) : (
          <span
            className={`block truncate text-center font-medium uppercase text-black ${nameText}`}
          >
            {nameLabel}
          </span>
        )}
      </div>

      {showRoomTag && booking.room_name && (
        <span
          className={`flex shrink-0 items-center gap-1 rounded-lg bg-[#10B018] font-semibold text-white ${pillPxTight} ${pillH} ${smallPillText}`}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            className={large ? "h-4 w-4" : "h-3.5 w-3.5"}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
            />
          </svg>
          {booking.room_name}
        </span>
      )}

      {booking.emergency && (
        <span
          className={`flex shrink-0 items-center justify-center whitespace-nowrap rounded-lg bg-[#C53030] font-semibold uppercase text-white ${pillPx} ${pillH} ${smallPillText}`}
        >
          Emergency
        </span>
      )}

      {booking.onhold ? (
        <span
          className={`flex shrink-0 items-center justify-center whitespace-nowrap rounded-lg bg-[#DC2626] font-semibold text-white ${pillPx} ${pillH} ${pillText}`}
        >
          On-Hold
        </span>
      ) : (
        showRightPill && (
          <span
            className={`flex shrink-0 items-center justify-center whitespace-nowrap rounded-lg font-semibold text-white ${pillPx} ${pillH} ${pillText}`}
            style={{ backgroundColor: statusColor }}
          >
            {rightLabel}
          </span>
        )
      )}
    </div>
  );
};

export default PatientRow;
